import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import moment from "moment";

import NutritionHeader from "./components/NutritionHeader";
import NutritionPlans from "./components/NutritionPlans";
import NutritionHistory from "./components/NutritionHistory";
import NutritionAdherence from "./components/NutritionAdherence";
import NutritionSummary from "./components/NutritionSummary";
import NutritionProgress from "./components/NutritionProgress";
import MyFitnessPalDiaryLink from "./components/MyFitnessPalDiaryLink";

import {
  getActiveDayStats,
  getAverageStats,
  getFilteredStats,
} from "../../../utils/nutrition-helpers";
import { resolveActiveBodyweightGoal } from "../../../utils/nutrition-progress";
import { AthleteLinks } from "../../../parts/athlete-links";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";

const Nutrition = ({ locals }) => {
  const {
    activeExercises,
    activeUser,
    clientGoals = [],
    currentNutritionPlan,
    dailyStats,
    dayPersonalRecords,
    habitLogs,
    nutritionPlans,
    nutritionTableTemplates = [],
    tdeeOverride = null,
    femaleCycleLogs,
    femaleCycleSettings,
    query,
    comesFromBodyComp,
    athlete,
    pathname,
    mealPhotos,
  } = locals;

  const todayUtc = Number(moment().format("YYYYMMDD"));
  const bodyweightGoal = resolveActiveBodyweightGoal(clientGoals, todayUtc);
  const { t } = useTranslation("athlete-portal");
  const startingCalendarDay = activeUser.startingCalendarDay || 0;
  const { tabView, dataView } = query;
  const [activeDayUtc, setActiveDayUtc] = useState(query.utcDate);
  const InitialDateFrom = currentNutritionPlan
    ? moment(String(currentNutritionPlan.utcStartDate)).toDate()
    : moment(String(activeDayUtc)).subtract(7, "days").toDate();
  const InitialDateTo = currentNutritionPlan
    ? moment(String(currentNutritionPlan.utcEndDate)).toDate()
    : moment(String(activeDayUtc)).toDate();

  const [selectedPlan, setSelectedPlan] = useState(
    currentNutritionPlan || null
  );

  const initialViewState = tabView ? tabView : "progress";
  const [viewMode, setViewMode] = useState(initialViewState);

  // New Progress screen vs the legacy table view. New is the default; the
  // choice persists in the URL (?progressView=) so it survives reloads.
  const initialProgressView =
    query.progressView === "classic" ? "classic" : "new";
  const [progressView, setProgressView] = useState(initialProgressView);

  // Change to single object when webpack changes
  const [dateFrom, setDateFrom] = useState(InitialDateFrom);
  const [dateTo, setDateTo] = useState(InitialDateTo);

  // Initial stats for when the user first enters the page
  const initialStats = getFilteredStats(
    dateFrom,
    dateTo,
    dailyStats,
    activeExercises
  );

  // Initial average stats for when the user first enters the page
  const initialAverageStats = getAverageStats(
    dateFrom,
    dateTo,
    startingCalendarDay,
    dailyStats
  );

  const [stats, setStats] = useState(initialStats);
  const [averageStats, setAverageStats] = useState(initialAverageStats);

  const initialActiveDayStats = getActiveDayStats(
    activeDayUtc,
    stats,
    averageStats
  );
  const [activeDayStats, setActiveDayStats] = useState(initialActiveDayStats);

  const [showAveragesOnly, setShowAveragesOnly] = useState(false);
  const [isActiveDayPeriod, setIsActiveDayPeriod] = useState(false);

  // Gets the new stats when the user changes the date
  useEffect(() => {
    const newStats = getFilteredStats(
      dateFrom,
      dateTo,
      dailyStats,
      activeExercises
    );
    const newAverages = getAverageStats(
      dateFrom,
      dateTo,
      startingCalendarDay,
      dailyStats
    );

    setStats(newStats);
    setAverageStats(newAverages);
  }, [dateFrom, dateTo]);

  // or if the user changes the client
  useEffect(() => {
    if (dataView !== "mealPhotos") {
      const newStats = getFilteredStats(
        dateFrom,
        dateTo,
        dailyStats,
        activeExercises
      );
      const newAverages = getAverageStats(
        dateFrom,
        dateTo,
        startingCalendarDay,
        dailyStats
      );

      setStats(newStats);
      setAverageStats(newAverages);
      setActiveDayStats(undefined);
    }
  }, [locals]);

  // Changes the date, when the webpack includes the spread operator change to single date object to use spread operator
  const handleDateChange = (date, field) => {
    if (field === "dateTo") {
      setDateTo(date);
    } else {
      setDateFrom(date);
    }
  };

  // Changes the plan in the selection
  // The conditional is for the empty select option
  const handleSelectedPlanChange = (value) => {
    if (value && value.length) {
      const selectedPlan = nutritionPlans.find(({ _id }) => _id === value);
      const dateFrom = moment(String(selectedPlan.utcStartDate)).toDate();
      const dateTo = moment(String(selectedPlan.utcEndDate)).toDate();
      setDateFrom(dateFrom);
      setDateTo(dateTo);
      setSelectedPlan(selectedPlan);
    }
  };

  const handleViewModeChange = (viewMode) => {
    setViewMode(viewMode);
  };

  // Toggle between the new Progress screen and the legacy table view, keeping
  // the choice in the URL without triggering a reload.
  const handleProgressViewChange = (view) => {
    setProgressView(view);
    if (typeof globalThis !== "undefined" && globalThis.history) {
      const url = new URL(globalThis.location.href);
      url.searchParams.set("progressView", view);
      globalThis.history.replaceState({}, "", url);
    }
  };

  // Gets the selected day stats when the user clicks on the table
  // Calls the getActiveDayStats helper
  const handleActiveDayChange = (activeDayUtc, isPeriod) => {
    const activeDayStats = getActiveDayStats(
      activeDayUtc,
      stats,
      averageStats,
      isPeriod
    );

    setActiveDayStats(activeDayStats);
    setActiveDayUtc(activeDayUtc);
    setIsActiveDayPeriod(isPeriod);
  };

  const handleShowAveragesOnly = (e) => {
    e.persist();
    const { checked } = e.target;
    setShowAveragesOnly(checked);
  };

  return (
    <main role="nutrition-wrapper" className="Nutrition">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NutritionHeader
        viewMode={viewMode}
        handleViewModeChange={handleViewModeChange}
        comesFromBodyComp={comesFromBodyComp}
      />
      {viewMode === "progress" ? (
        <div className="Nutrition-Progress">
          <div className="flex flex-wrap items-center justify-end gap-3 mb-3">
            <MyFitnessPalDiaryLink
              athleteId={athlete?._id}
              activeUser={activeUser}
            />
            <div className="toggle-options np-view-toggle">
              <button
                data-button-type={progressView === "new" ? "outline" : "text-white"}
                onClick={() => handleProgressViewChange("new")}
              >
                <span>{t("nutritionPage.header.viewNew")}</span>
              </button>
              <button
                data-button-type={progressView === "classic" ? "outline" : "text-white"}
                onClick={() => handleProgressViewChange("classic")}
              >
                <span>{t("nutritionPage.header.viewLegacy")}</span>
              </button>
            </div>
          </div>
          {progressView === "new" ? (
            <NutritionProgress
              nutritionPlans={nutritionPlans}
              nutritionTableTemplates={nutritionTableTemplates}
              currentNutritionPlan={currentNutritionPlan}
              dailyStats={dailyStats}
              activeExercises={activeExercises}
              dayPersonalRecords={dayPersonalRecords}
              habitLogs={habitLogs}
              femaleCycleLogs={femaleCycleLogs}
              femaleCycleSettings={femaleCycleSettings}
              athlete={athlete}
              activeUser={activeUser}
              bodyweightGoal={bodyweightGoal}
              tdeeOverride={tdeeOverride}
            />
          ) : (
            <>
              <NutritionHistory
                activeDayUtc={activeDayUtc}
                dateFrom={dateFrom}
                dateTo={dateTo}
                stats={stats}
                averageStats={averageStats}
                nutritionPlans={nutritionPlans}
                selectedPlan={selectedPlan}
                handleDateChange={handleDateChange}
                handleActiveDayChange={handleActiveDayChange}
                handleSelectedPlanChange={handleSelectedPlanChange}
                activeUser={activeUser}
                isActiveDayPeriod={isActiveDayPeriod}
                showAveragesOnly={showAveragesOnly}
                handleShowAveragesOnly={handleShowAveragesOnly}
                activeExercises={activeExercises}
              />

              <div className="history-summary">
                <NutritionAdherence
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  stats={stats}
                  nutritionPlans={nutritionPlans}
                  averageStats={averageStats}
                  activeExercises={activeExercises}
                />
                <NutritionSummary
                  activeDayUtc={activeDayUtc}
                  dayStats={activeDayStats}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  stats={stats}
                  withToggle
                  nutritionPlans={nutritionPlans}
                  activeExercises={activeExercises}
                  query={query}
                  mealPhotos={mealPhotos}
                  locals={locals}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <NutritionPlans
          nutritionPlans={nutritionPlans}
          activeUser={activeUser}
          athlete={athlete}
          dailyStats={dailyStats}
        />
      )}
    </main>
  );
};

export default Nutrition;
